import { FC, useMemo } from 'react'

import {
  Text,
  IconExclamationTriangle,
  BorderRadius6,
  GreyGrey10,
  GreyGrey70,
  GreyGrey80,
  Primary12Regular,
  Primary13SemiBold,
  RedRed60,
  Flex
} from '@sequencehq/core-components'
import { useAtom } from 'jotai'
import { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'
import { isAutomationPresentation } from 'Automations/nodeDefinitions/guards'
import {
  hasBlockingValidationResults,
  validatePresentation
} from 'Automations/nodeDefinitions'
import { formatTimestamp } from 'Automations/helpers'
import {
  useResolvedNodeDefinitions,
  versionPreviewAtom
} from 'Automations/automationEditor/atoms'
import { PublishedBadge } from 'Automations/components'
import {
  SYSTEM_AUTHOR_ID,
  SYSTEM_AUTHOR_LABEL
} from './versionHistory.constants'

type AutomationVersion = Dashboardv99990101Api.Automations.AutomationVersion

type Props = {
  automationVersion: AutomationVersion
  isStatic?: boolean
  onClick?: () => void
}

export const VersionHistoryItem: FC<Props> = ({
  automationVersion,
  isStatic = false,
  onClick
}) => {
  const [versionPreview, setVersionPreview] = useAtom(versionPreviewAtom)
  const definitions = useResolvedNodeDefinitions()

  // Validity is computed synchronously from the resolved registry (the editor
  // only renders this once the registry is populated).
  const isInvalid = useMemo(() => {
    const presentation = automationVersion.presentation

    if (!presentation || !isAutomationPresentation(presentation)) return true

    return hasBlockingValidationResults(
      validatePresentation(presentation, definitions)
    )
  }, [automationVersion.presentation, definitions])

  const isClickable = !isStatic || !!onClick

  const isSystemAuthor = automationVersion.createdBy === SYSTEM_AUTHOR_ID

  return (
    <Flex
      data-testid={`automations.editor.versionHistory.item.${automationVersion.id}`}
      padding="8px"
      gap="8px"
      width="100%"
      borderRadius={BorderRadius6}
      onClick={() => {
        if (onClick) {
          onClick()
          return
        }

        if (isStatic) return

        const presentation = automationVersion.presentation

        if (!presentation || !isAutomationPresentation(presentation)) {
          throw new Error('presentation for this version is invalid')
        }

        setVersionPreview(automationVersion)
      }}
      {...(isClickable
        ? { _hover: { backgroundColor: GreyGrey10, cursor: 'pointer' } }
        : {})}
      background={
        versionPreview?.id === automationVersion.id ? GreyGrey10 : undefined
      }
      transition="background 250ms"
    >
      <Flex gap="4px" flexDir="column" flex="1" minWidth="0">
        <Flex gap="8px">
          <Text
            data-testid="automations.editor.versionHistory.item.timestamp"
            {...Primary13SemiBold}
            color={GreyGrey80}
          >
            {formatTimestamp(automationVersion.createdAt)}
          </Text>
          {isInvalid && (
            <IconExclamationTriangle
              data-testid="automations.editor.versionHistory.item.invalidIcon"
              color={RedRed60}
            />
          )}
        </Flex>

        {isSystemAuthor ? (
          <Text
            data-testid="automations.editor.versionHistory.item.system"
            {...Primary12Regular}
            color={GreyGrey70}
            noOfLines={1}
          >
            {SYSTEM_AUTHOR_LABEL}
          </Text>
        ) : (
          automationVersion.createdByEmail && (
            <Text
              data-testid="automations.editor.versionHistory.item.email"
              {...Primary12Regular}
              color={GreyGrey70}
              noOfLines={1}
            >
              {automationVersion.createdByEmail}
            </Text>
          )
        )}
      </Flex>

      {automationVersion.active && <PublishedBadge status="published" />}
    </Flex>
  )
}
